import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './Toast';
import type { ToastInstance } from '../contexts/ToastContext';

/**
 * Toasts ficam num <div popover="manual"> + showPopover() pra entrar no
 * top-layer do browser — acima de qualquer modal `<dialog showModal()>`,
 * que tambem usa top-layer. Top-layer empilha por ordem de insercao: o
 * ultimo a entrar fica em cima. A cada mudanca na lista re-inserimos o
 * viewport (hide + show) pra garantir que ele suba pro topo acima de
 * qualquer modal aberto.
 *
 * Por que nao <dialog>? `dialog.show()` (non-modal) NAO entra no top-
 * layer pela spec — apenas showModal() e showPopover() entram. A Popover
 * API foi feita exatamente pra esse caso (overlays nao-modais).
 *
 * Suporte: Chrome 114+, Safari 17+, Firefox 125+.
 */
export function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastInstance[];
  onDismiss: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (toasts.length === 0) {
      try { el.hidePopover(); } catch { /* not open */ }
      return;
    }

    // A cada mudanca na lista (add, dismiss, replace), re-insere o
    // popover no FIM do top-layer pra ficar acima de qualquer modal.
    try { el.hidePopover(); } catch { /* not open */ }
    // showPopover pode nao existir (browser antigo sem Popover API) ou lancar se
    // ja aberto. Sem esse guard, o toast derrubava o render inteiro (TypeError:
    // showPopover is not a function). Sem a API o <div> ainda renderiza via
    // position:fixed + z-index — so nao garante ficar acima de modais opacos.
    try { el.showPopover(); } catch { /* nao suportado / ja aberto */ }
  }, [toasts]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={ref}
      popover="manual"
      style={{
        position: 'fixed',
        top: 'calc(var(--topbar-height) + var(--space-3))',
        right: 'var(--space-4)',
        left: 'auto',
        bottom: 'auto',
        margin: 0,
        padding: 0,
        border: 0,
        background: 'transparent',
        color: 'inherit',
        maxWidth: 'none',
        maxHeight: 'none',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        zIndex: 'var(--z-toast)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <Toast
          key={t.id}
          tone={t.tone}
          title={t.title}
          message={t.message}
          action={t.action}
          onDismiss={() => onDismiss(t.id)}
        />
      ))}
    </div>,
    document.body,
  );
}
